import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Car, Fuel, Gauge, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VehicleCatalogPage() {
  const [vehicles, setVehicles] = useState([]);
  const [filter, setFilter] = useState("todos");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const params = {};
        if (filter !== "todos") params.vehicle_type = filter;
        params.status = "disponible";
        const res = await axios.get(`${API}/vehicles`, { params });
        setVehicles(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, [filter]);

  return (
    <div className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <span className="text-xs uppercase tracking-widest text-red-600 font-semibold">Nuestro stock</span>
            <h1 className="heading-industrial text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 uppercase" data-testid="vehicles-page-title">
              Vehiculos en venta
            </h1>
            <p className="text-slate-500 mt-2 text-base">Todos nuestros vehiculos revisados y con garantia.</p>
          </div>
          <div className="w-48">
            <Select value={filter} onValueChange={setFilter} data-testid="vehicle-filter-select">
              <SelectTrigger className="rounded-sm border-slate-200 h-10" data-testid="vehicle-filter-trigger">
                <SelectValue placeholder="Filtrar por tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="km0">KM0</SelectItem>
                <SelectItem value="usado">Usados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="h-80 bg-slate-100 animate-pulse rounded-sm" />
            ))}
          </div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-24" data-testid="no-vehicles-message">
            <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="heading-industrial text-xl font-bold text-slate-400 uppercase">No hay vehiculos disponibles</h3>
            <p className="text-sm text-slate-400 mt-2">Vuelve pronto, actualizamos el stock frecuentemente.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((v) => (
              <Link to={`/vehiculos/${v.id}`} key={v.id} className="block group">
                <Card className="border-slate-200 rounded-sm overflow-hidden hover:border-slate-400 transition-colors" data-testid={`vehicle-card-${v.id}`}>
                <div className="h-48 bg-slate-100 relative overflow-hidden">
                  {v.images && v.images.length > 0 ? (
                    <img src={`${API}/files/${v.images[0].path}`} alt={`${v.brand} ${v.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Car className="w-16 h-16 text-slate-300" />
                    </div>
                  )}
                  <Badge className={`absolute top-3 left-3 rounded-sm text-xs uppercase ${v.vehicle_type === 'km0' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'}`}>
                    {v.vehicle_type === 'km0' ? 'KM0' : 'Usado'}
                  </Badge>
                </div>
                <CardContent className="p-5">
                  <h3 className="heading-industrial text-lg font-bold text-slate-900 uppercase">{v.brand} {v.model}</h3>
                  <p className="text-2xl font-bold text-red-600 font-mono mt-1">{v.price?.toLocaleString('es-ES')} &euro;</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Gauge className="w-3.5 h-3.5" /> {v.mileage?.toLocaleString('es-ES')} km</span>
                    <span className="flex items-center gap-1"><Fuel className="w-3.5 h-3.5" /> {v.fuel_type}</span>
                    <span className="flex items-center gap-1"><Settings2 className="w-3.5 h-3.5" /> {v.transmission}</span>
                  </div>
                  {v.year && <span className="text-xs text-slate-400 mt-2 block font-mono">{v.year}</span>}
                </CardContent>
              </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
